import React, { useState, useCallback, useEffect} from "react";
import { Map, Marker, Overlay, GeoJson } from "pigeon-maps";
import Title from "./Title";
import NumCaptures from "./NumCaptures";
import CaptureTable from "./CaptureTable";
import SearchButton from "./SearchButton";
import FilterSection from "./FilterSection";
import { osm, stamenTerrain } from 'pigeon-maps/providers'
import MapProviderToggle from './MapProviderToggle';
import ScenarioInfo, { getScenarioLabel } from "./ScenarioInfo";
import satSpecs from "./sat_db/satSpecs";
import BoxLegend from "./BoxLegend"
import ScaleBar from "./ScaleBar";
import { getBoxColor, getStrokeColor } from "./BoundingBoxUtils";
import { fitBboxToMapView, getLargestBbox } from "./MapViewUtils";
import {
  removeDuplicateCaptures,
  calculateTimeDiffs,
  getNonSarCloudCover,
  hasHourlyCloudCover,
} from "./CaptureMetrics";
import {Button } from "@material-ui/core";
import "./DynamicMap.css";
import "./FilterSection.css";


const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || "";

const getScenarioDateRange = (scenario) => ({
  startDate: scenario === "case1" ? "2021-01-01T00:00:00" : "2022-06-01T00:00:00",
  endDate: scenario === "case1" ? "2021-01-31T23:59:59" : "2022-06-01T23:59:59",
});

const getCaptureKey = (id, timestamp) => `${id}-${timestamp}`;

const calculateRevisitStats = (captures) => {
  if (captures.length <= 1) {
    return { avg: 0, max: 0 };
  }

  const timeDiffs = calculateTimeDiffs(captures);
  if (timeDiffs.length === 0) {
    return { avg: 0, max: 0 };
  }

  const avg = timeDiffs.reduce((a, b) => a + b, 0) / timeDiffs.length;
  const max = Math.max(...timeDiffs);

  return {
    avg: avg.toFixed(2),
    max: max.toFixed(2),
  };
};

const formatMetricNumber = (value) => (
  Number.isInteger(value) ? String(value) : value.toFixed(2).replace(/\.?0+$/, "")
);

const calculateBestResolution = (captures) => {
  const resolutions = captures
    .map(([id]) => Number(satSpecs[id]?.resolution))
    .filter(Number.isFinite);

  if (resolutions.length === 0) {
    return "NA";
  }

  return formatMetricNumber(Math.min(...resolutions));
};

const calculateBestCloudCover = (captures, hourlyCloudData) => {
  if (!hasHourlyCloudCover(hourlyCloudData)) {
    return "NA";
  }

  const cloudCovers = captures
    .map(([id, timestamp]) => getNonSarCloudCover(id, timestamp, hourlyCloudData))
    .filter((cloudCover) => cloudCover !== null);

  if (cloudCovers.length === 0) {
    return "NA";
  }

  return formatMetricNumber(Math.min(...cloudCovers));
};

function DynamicMap(props) {
  const [markerPosition, setMarkerPosition] = useState(null);
  const [overlayVisible, setOverlayVisible] = useState(false);
  const [boundingBoxesData, setBoundingBoxesData] = useState([]);
  const [avgRevisitTime, setAvgRevisitTime] = useState(0);
  const [maxRevisitTime, setMaxRevisitTime] = useState(0);
  const [filter, setFilter] = useState({
    startDate: "",
    endDate: "",
    constellation: "All",
    resolution: null,
    spectralType: "All"
  });  
  const [mapProvider, setMapProvider] = useState('osm');
  const [scenario, setScenario] = useState("case1");
  const [highlightedBox, setHighlightedBox] = useState(null);
  const [filteredBoundingBoxesData, setFilteredBoundingBoxesData] = useState([]);
  const [cloudData, setCloudData] = useState({})
  const [bestResolution, setBestResolution] = useState("NA");
  const [bestCloudCover, setBestCloudCover] = useState("NA");
  const [mapCenter, setMapCenter] = useState([23, 0]);
  const [mapZoom, setMapZoom] = useState(2.85);

  useEffect(() => {
    setFilter((prevFilter) => ({
      ...prevFilter,
      ...getScenarioDateRange(scenario),
    }));
  }, [scenario]);

  function handleRowMouseEnter(id, timestamp) {
    setHighlightedBox(getCaptureKey(id, timestamp));
  }
  
  function handleRowMouseLeave() {
    setHighlightedBox(null);
  }

  const toggleScenario = () => {
    setScenario((prevScenario) => (prevScenario === "case1" ? "case2" : "case1"));
  };

  const providerFunction = (x, y, z, dpr) => {
    if (mapProvider === 'osm') {
      return osm(x, y, z, dpr);
    } else if (mapProvider === 'stamenTerrain') {
      return stamenTerrain(x, y, z, dpr);
    }
  };

  const handleMapClick = useCallback(({ latLng }) => {
    setMarkerPosition(latLng);
    setOverlayVisible(true);
  }, []);

  const handleFilterChange = useCallback((event) => {
    const { name, value } = event.target;
    setFilter((prevFilter) => ({ ...prevFilter, [name]: value }));
  }, []);

  const applyFilter = useCallback((captures) => {
    const { startDate, endDate, constellation, resolution, spectralType } = filter;
  
    return captures.filter(([id, timestamp]) => {
      const captureDate = new Date(timestamp);
      const satSpec = satSpecs[id];
      if (!satSpec) {
        return false;
      }

      const resolutionFilterPassed =
        !Array.isArray(resolution) ||
        (
          satSpec.resolution >= Number(resolution[0]) &&
          satSpec.resolution <= Number(resolution[1])
        );
      
      const dateFilterPassed =
        (!startDate || new Date(startDate + "Z") <= captureDate) &&
        (!endDate || new Date(endDate + "Z") >= captureDate);

      const constellationFilterPassed =
        !constellation || constellation === "All" || satSpec.constellation === constellation;
  
      const spectralTypeFilterPassed =
        !spectralType || spectralType === "All" || satSpec.spectral_range === spectralType;

      if (scenario === "case1") {
        return dateFilterPassed;
      }
  
      return (
        dateFilterPassed &&
        constellationFilterPassed &&
        resolutionFilterPassed &&
        spectralTypeFilterPassed
      );
    });
  }, [filter, scenario]);

  useEffect(() => {
    const filteredDataByDateRange = applyFilter(boundingBoxesData);
    setFilteredBoundingBoxesData(filteredDataByDateRange);
    const { avg, max } = calculateRevisitStats(filteredDataByDateRange);
    setAvgRevisitTime(avg);
    setMaxRevisitTime(max);
    setBestResolution(calculateBestResolution(filteredDataByDateRange));
  }, [applyFilter, boundingBoxesData]);

  useEffect(() => {
    setBestCloudCover(calculateBestCloudCover(filteredBoundingBoxesData, cloudData.hourly));
  }, [filteredBoundingBoxesData, cloudData.hourly]);

  useEffect(() => {
    const largestBbox = getLargestBbox(filteredBoundingBoxesData);
    const fittedView = fitBboxToMapView(largestBbox, {
      width: window.innerWidth,
      height: window.innerHeight,
    });

    if (fittedView) {
      setMapCenter(fittedView.center);
      setMapZoom(fittedView.zoom);
    }
  }, [filteredBoundingBoxesData]);

  const handleBoundsChanged = useCallback(({ center, zoom }) => {
    setMapCenter(center);
    setMapZoom(zoom);
  }, []);
  
    //this only works for archive data at the moment -- for current and future, see openmeteo docs
async function fetchCloudData(lat,lon){
  try {
    //api accepts dates in YYYY/MM/DD
    const start = filter.startDate.substring(0,10)
    const end = filter.endDate.substring(0,10)
    const cloud_response = await fetch(
      `https://archive-api.open-meteo.com/v1/archive?latitude=${lat}&longitude=${lon}&start_date=${start}&end_date=${end}&hourly=cloudcover`
    );  
    const cloud_data = await cloud_response.json();
    setCloudData(cloud_data)
  }
  catch (error){
    console.error("Error fetching weather data:", error);
  }
}

  async function fetchApiData() {
    try {
      setOverlayVisible(false);
      setCloudData({});
      const [lat, lon] = markerPosition;
      const response = await fetch(
        `${apiBaseUrl}/${scenario}search?lat=${lat}&lon=${lon}`
      );      
      const data = await response.json();
      const dedupedData = removeDuplicateCaptures(data);
      setBoundingBoxesData(dedupedData);
      fetchCloudData(lat,lon)
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  }  
    
  const geoJsonData = {
    type: "FeatureCollection",
    features: filteredBoundingBoxesData.map((item) => {
      const [id, timestamp, coords] = item;
      return {
        type: "Feature",
        properties: { id, timestamp },
        geometry: {
          type: "Polygon",
          coordinates: [
            [
              [coords[0], coords[1]],
              [coords[2], coords[1]],
              [coords[2], coords[3]],
              [coords[0], coords[3]],
              [coords[0], coords[1]],
            ],
          ],
        },
      };
    }),
  };

  return (
    <div className="dynamic-map-container">
      <Map
        provider={providerFunction}
        center={mapCenter}
        zoom={mapZoom}
        minZoom={2}
        maxZoom={12}
        animate
        onBoundsChanged={handleBoundsChanged}
        onClick={handleMapClick}
      >
    <div className="title-container">
    <Title />
  </div>
        <MapProviderToggle mapProvider={mapProvider} setMapProvider={setMapProvider} />
        <div className="map-top-left-container">
    <ScenarioInfo scenario={scenario} toggleScenario={toggleScenario}/>
  <FilterSection
    filter={filter}
    handleFilterChange={handleFilterChange}
    scenario={scenario}
  />
</div>
        {boundingBoxesData.length === 0 && (
          <div className="map-empty-state">
            <div className="empty-state-title">Likely access for Earth observing spacecraft</div>
            <div className="empty-state-copy">
              Click a target area, then run a search to evaluate candidate access for the selected scenario.
            </div>
          </div>
        )}

        {markerPosition && <Marker anchor={markerPosition} />}
        {markerPosition && overlayVisible && (
           <Overlay anchor={markerPosition} offset={[60, 0]}>
           <SearchButton fetchApiData={fetchApiData} markerPosition={markerPosition} />
         </Overlay>
        )}
<GeoJson
  data={geoJsonData}
  styleCallback={(feature) => {
    const id = feature.properties.id;
    const timestamp = feature.properties.timestamp;
    const isHighlighted = highlightedBox === getCaptureKey(id, timestamp);
    return {
      fill: getBoxColor(id, satSpecs),
      stroke: isHighlighted ? "rgba(255, 255, 255, 1)" : getStrokeColor(id, satSpecs),
      strokeWidth: isHighlighted ? "5" : "2",
      zIndex: isHighlighted ? 1000 : 1,
    };
  }}
/>
<BoxLegend />
<ScaleBar center={mapCenter} zoom={mapZoom} />
      </Map>
      <div className="map-side-panel">
        <NumCaptures
          scenarioLabel={getScenarioLabel(scenario)}
          numCaptures={filteredBoundingBoxesData.length}
          avgRevisitTime={avgRevisitTime}
          maxRevisitTime={maxRevisitTime}
          bestResolution={bestResolution}
          bestCloudCover={bestCloudCover}
        />
        {boundingBoxesData.length > 0 && (
          <>
            <CaptureTable
              boundingBoxesData={filteredBoundingBoxesData}
              cloudData={cloudData}
              handleRowMouseEnter={handleRowMouseEnter}
              handleRowMouseLeave={handleRowMouseLeave}
            />
            <div className="footprint-note">
              Boxes show approximate footprint opportunities; the selected point may be anywhere within a valid footprint.
            </div>
          </>
        )}
      </div>
              <div
        style={{
          position: "absolute",
          top: "12%",
          left: "50%",
          transform: "translate(-50%, -50%)"
        }}
      >
<Button
  variant="contained"
  color="primary"
  style={{
    background: "transparent",
    border: "none",
    boxShadow: "0 0 10px rgba(255, 255, 255, 0.3)",
    backdropFilter: "blur(10px)",
    color: "white",
    padding: "10px 20px",
    borderRadius: "20px",
  }}
  onClick={props.onHome}
>
  Home
</Button>

      </div>
    </div>
    
  );
}

export default DynamicMap;
